'use client'

/**
 * The "time passes" banner (docs/demo-autopilot.md, Screens). Mounted once by
 * the shell, so it shows on every page and on the stage window. When the
 * autopilot moves the story, `autopilot.banner.shown_at` changes; the banner
 * then slides in at the top of the content area with the new story time large
 * and the banner sentence, stays 8 seconds and settles up towards the header
 * clock pill. Hover or keyboard focus holds it (WCAG 2.2.1). Nothing shows
 * for the banner the window found on its first load, nor before the
 * earthquake. Screen readers hear it once through a polite live region; with
 * reduced motion it appears and leaves without moving.
 */

import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { STORY_START_STEP, useStory } from '@/lib/story'
import { fmtElapsed } from '@/lib/elapsed'
import { REVEAL_EASE } from './Reveal'

/** How long the banner stays before it settles. */
export const BANNER_SHOW_MS = 8000

interface ShownBanner {
  key: string
  elapsed: string
  /** Words before the time in the sentence ("Next morning"), else null. */
  eyebrow: string | null
  body: string
}

/**
 * "+34 min: 6 of 8 agencies acknowledged." becomes the large "+34 min" and the
 * sentence "6 of 8 agencies acknowledged."; a sentence that does not start
 * with the time ("Earthquake detected: ...") stays whole.
 */
function splitBanner(text: string, times: string[]): { eyebrow: string | null; body: string } {
  const cut = text.indexOf(': ')
  if (cut > 0) {
    const head = text.slice(0, cut)
    const time = times.find((t) => t && head.includes(t))
    if (time) {
      const eyebrow = head.replace(time, '').replace(/[\s,]+$/, '').trim()
      const rest = text.slice(cut + 2).trim()
      return { eyebrow: eyebrow || null, body: rest.charAt(0).toUpperCase() + rest.slice(1) }
    }
  }
  return { eyebrow: null, body: text }
}

export function TimePassesBanner() {
  const { story } = useStory()
  const reduced = useReducedMotion()
  // The banner this window has already seen; undefined until the first answer.
  const seen = useRef<string | null | undefined>(undefined)
  const [shown, setShown] = useState<ShownBanner | null>(null)
  const [announcement, setAnnouncement] = useState<{ key: string; text: string } | null>(null)
  // Hover or focus inside the banner holds it; the time left carries over.
  const [held, setHeld] = useState(false)
  const left = useRef<{ key: string; ms: number }>({ key: '', ms: BANNER_SHOW_MS })

  useEffect(() => {
    if (!story) return
    const banner = story.autopilot?.banner ?? null
    const at = banner?.shown_at ?? null
    if (seen.current === undefined) {
      seen.current = at
      return
    }
    if (!banner || !at || at === seen.current) return
    seen.current = at
    // No banner before the first alert.
    if (story.clock_min < 0 || story.step_id === STORY_START_STEP) return
    const elapsed = fmtElapsed(story.clock_min) ?? story.at_label ?? ''
    const { eyebrow, body } = splitBanner(banner.text, [elapsed, story.at_label ?? ''])
    setShown({ key: at, elapsed, eyebrow, body })
    setHeld(false)
    setAnnouncement({ key: at, text: `${eyebrow ? `${eyebrow}, ` : ''}${elapsed}. ${body}` })
  }, [story])

  // Settle after 8 seconds of being shown and not held.
  useEffect(() => {
    if (!shown || held) return
    if (left.current.key !== shown.key) left.current = { key: shown.key, ms: BANNER_SHOW_MS }
    const startedAt = Date.now()
    const timer = setTimeout(() => setShown(null), left.current.ms)
    return () => {
      clearTimeout(timer)
      if (left.current.key === shown.key) left.current.ms = Math.max(0, left.current.ms - (Date.now() - startedAt))
    }
  }, [shown, held])

  return (
    <>
      <div role='status' aria-live='polite' aria-atomic='true' className='sr-only'>
        {/* A new node per banner, so a repeated sentence is still announced. */}
        {announcement && <p key={announcement.key}>{announcement.text}</p>}
      </div>
      <div className='pointer-events-none fixed left-4 right-4 top-[var(--shell-sticky-top)] z-fixed flex justify-center md:left-[calc(var(--shell-rail-width)+1rem)]'>
        <AnimatePresence mode='wait'>
          {shown && (
            <motion.div
              key={shown.key}
              initial={reduced ? { opacity: 1 } : { opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0, x: 0, scale: 1 }}
              exit={reduced ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, y: -28, x: 64, scale: 0.94 }}
              transition={{ duration: reduced ? 0 : 0.42, ease: REVEAL_EASE }}
              onMouseEnter={() => setHeld(true)}
              onMouseLeave={() => setHeld(false)}
              onFocus={() => setHeld(true)}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHeld(false)
              }}
              className='aio-banner pointer-events-auto relative w-full max-w-3xl overflow-hidden rounded-2xl border border-hairline bg-card shadow-float'
            >
              <div className='flex items-start gap-4 px-5 py-4'>
                <span aria-hidden='true' className='mt-1 h-12 w-1 shrink-0 rounded-full bg-needs-you' />
                {/* The live region above speaks the sentence; this copy is for the eye. */}
                <div aria-hidden='true' className='min-w-0 flex-1'>
                  <p className='text-xs font-semibold uppercase tracking-widest text-muted-foreground'>
                    {shown.eyebrow ?? 'Time passes'}
                  </p>
                  <p className='font-display text-3xl font-semibold tabular-nums text-foreground lg:text-4xl'>{shown.elapsed}</p>
                  <p className='mt-1 text-base text-foreground'>{shown.body}</p>
                </div>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon-sm'
                  onClick={() => setShown(null)}
                  aria-label='Dismiss the time passes banner'
                  className='-mr-2 -mt-1 shrink-0 text-muted-foreground hover:text-foreground'
                >
                  <Icons.close className='h-4 w-4' strokeWidth={1.5} aria-hidden='true' />
                </Button>
              </div>
              <span
                aria-hidden='true'
                className='aio-banner-drain absolute bottom-0 left-0 h-0.5 w-full bg-brand-cornflower'
                style={{ ['--banner-duration' as string]: `${BANNER_SHOW_MS}ms` }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}
